import React, { useState, useRef, useEffect } from 'react';
import KanbanCard from './KanbanCard';

function CardComposer({ onAdd, onClose }) {
  const [title, setTitle] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const textareaRef = useRef(null);

  const submit = async () => {
    const trimmed = title.trim();
    if (!trimmed || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await onAdd(trimmed);
      // Keep the composer open so several cards can be added in a row
      setTitle('');
      textareaRef.current?.focus();
    } catch (err) {
      console.error('Error adding card:', err);
      alert('Failed to add card. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <div className="rounded-lg bg-neutral-800 border border-neutral-700/50 p-2">
      <textarea
        ref={textareaRef}
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Enter a title for this card…"
        rows="2"
        autoFocus
        className="w-full bg-transparent text-sm text-neutral-100 placeholder-neutral-500 resize-none outline-none"
      />
      <div className="flex items-center gap-1.5 mt-1">
        <button
          onClick={submit}
          disabled={!title.trim() || isSubmitting}
          className="px-2.5 py-1 rounded-md bg-neutral-200 hover:bg-white text-neutral-900 text-xs font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {isSubmitting ? 'Adding…' : 'Add card'}
        </button>
        <button
          onClick={onClose}
          className="px-2 py-1 rounded-md text-neutral-400 hover:text-neutral-200 hover:bg-neutral-700/60 text-xs transition-colors"
          title="Cancel"
        >
          ✕
        </button>
      </div>
    </div>
  );
}

export default function KanbanList({
  columnName,
  items,
  isEditable,
  onCardDrop,
  onCardClick,
  onAddCard
}) {
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [composerOpen, setComposerOpen] = useState(false);

  // Clear any stale drop indicator once a drop has been applied
  useEffect(() => {
    setDragOverIndex(null);
  }, [items]);

  const handleMoveToTop = (itemId) => {
    if (!onCardDrop || items.length === 0 || items[0].id === itemId) return;
    onCardDrop(itemId, columnName, items[0].id);
  };

  const handleMoveToBottom = (itemId) => {
    if (!onCardDrop || items.length === 0 || items[items.length - 1].id === itemId) return;
    onCardDrop(itemId, columnName, null);
  };

  const handleDragOver = (e) => {
    if (!isEditable) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    const cards = e.currentTarget.querySelectorAll('[data-card-id]');
    const y = e.clientY;
    let index = cards.length;
    for (let i = 0; i < cards.length; i++) {
      const rect = cards[i].getBoundingClientRect();
      if (y < rect.top + rect.height / 2) {
        index = i;
        break;
      }
    }
    setDragOverIndex(index);
  };

  const handleDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverIndex(null);
    }
  };

  const handleDrop = (e) => {
    if (!isEditable) return;
    e.preventDefault();
    const index = dragOverIndex;
    setDragOverIndex(null);

    let itemId = e.dataTransfer.getData('text/plain');
    try {
      const dragData = JSON.parse(e.dataTransfer.getData('application/json'));
      itemId = dragData.id || itemId;
    } catch (err) {
      // Fall back to the plain-text id
    }
    if (!itemId || !onCardDrop) return;

    // Resolve the card the drop lands before; skip over the dragged card
    // itself so dropping onto its own slot is a no-op instead of a jump.
    let beforeId = null;
    if (index !== null) {
      let j = index;
      if (j < items.length && items[j].id === itemId) j += 1;
      if (j < items.length) beforeId = items[j].id;
    }

    onCardDrop(itemId, columnName, beforeId);
  };

  return (
    <div
      data-column={columnName}
      className="w-[calc(100vw-2.5rem)] md:w-[272px] shrink-0 flex flex-col max-h-full h-full rounded-xl bg-neutral-900 border border-neutral-800 snap-start snap-always md:snap-align-none"
    >
      {/* List header */}
      <div className="flex items-center justify-between px-3 pt-2.5 pb-1.5 shrink-0">
        <h2 className="text-[13px] font-medium text-neutral-300">{columnName}</h2>
        <span className="text-[11px] text-neutral-500 tabular-nums">{items.length}</span>
      </div>

      {/* Add card — kept above the scrollable list so it's reachable on long lists */}
      {isEditable && (
        <div className="px-2 pb-1.5 shrink-0">
          {composerOpen ? (
            <CardComposer
              onAdd={(title) => onAddCard(title, columnName)}
              onClose={() => setComposerOpen(false)}
            />
          ) : (
            <button
              onClick={() => setComposerOpen(true)}
              className="w-full text-left px-2.5 py-1.5 rounded-lg text-[13px] text-neutral-500 hover:text-neutral-200 hover:bg-neutral-800 transition-colors touch-manipulation"
            >
              + Add a card
            </button>
          )}
        </div>
      )}

      {/* Cards */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className="flex-1 min-h-[8px] overflow-y-auto overscroll-contain px-2 pb-2 space-y-2"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        {items.map((item, index) => (
          <React.Fragment key={item.id}>
            {dragOverIndex === index && (
              <div className="h-9 rounded-lg bg-neutral-700/40" />
            )}
            <KanbanCard
              item={item}
              isEditable={isEditable}
              onClick={onCardClick}
              canMoveToTop={index > 0}
              canMoveToBottom={index < items.length - 1}
              onMoveToTop={() => handleMoveToTop(item.id)}
              onMoveToBottom={() => handleMoveToBottom(item.id)}
              onMoveToInProgress={() => onCardDrop?.(item.id, 'In Progress', null)}
              onMoveToDone={() => onCardDrop?.(item.id, 'Done', null)}
            />
          </React.Fragment>
        ))}
        {dragOverIndex === items.length && (
          <div className="h-9 rounded-lg bg-neutral-700/40" />
        )}
      </div>
    </div>
  );
}
